import React, { useEffect, useState } from 'react';
import { Store, ExternalLink, MapPin, Mail, Phone, Info, ChevronDown, ChevronUp, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { api, fileUrl, qrUrl } from '@/lib/api';
import { AAWTA } from '@/lib/aawta';
import ShareMenu from './ShareMenu';

const buildProfileUrl = (id) => {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  return `${origin}/#exhibitor-${id}`;
};

const buildShareText = (e) => {
  const parts = [e.name];
  if (e.booth_number) parts.push(`Booth ${e.booth_number}`);
  if (e.description) parts.push(e.description.length > 180 ? e.description.slice(0, 180).trimEnd() + '…' : e.description);
  return parts.join(' · ');
};

const normalizeUrl = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};
const openExternal = (url) => url && window.open(url, '_blank', 'noopener,noreferrer');

const ExhibitorDesc = ({ description, id }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = description.length > 180;
  const preview = isLong ? description.slice(0, 180).trimEnd() + '…' : description;
  return (
    <div className="mt-3">
      <p
        data-testid={`exhibitor-description-${id}`}
        className={`text-[14px] text-aawta-muted leading-relaxed whitespace-pre-wrap ${expanded ? 'max-h-[40vh] overflow-y-auto pr-1' : ''}`}
      >
        {expanded ? description : preview}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`exhibitor-readmore-${id}`}
          className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
        >
          {expanded ? (<>Read Less <ChevronUp className="h-3.5 w-3.5" /></>) : (<>Read More <ChevronDown className="h-3.5 w-3.5" /></>)}
        </button>
      )}
    </div>
  );
};

const FullProfile = ({ e, onClose }) => {
  const website = normalizeUrl(e.website);
  return (
    <Dialog open={!!e} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <div className="flex items-start gap-4">
            {e.logo_file_id ? (
              <img src={fileUrl(e.logo_file_id)} alt={e.name} className="w-20 h-20 rounded-xl object-contain bg-white border border-aawta-border p-1.5" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-aawta-surface border border-aawta-border flex items-center justify-center"><Store className="h-8 w-8 text-aawta-navy/40" /></div>
            )}
            <div className="flex-1">
              <DialogTitle className="font-display text-2xl text-aawta-navy">{e.name}</DialogTitle>
              <DialogDescription className="mt-1">
                {e.industry && <span className="mr-2">{e.industry}</span>}
                {e.booth_number && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[10.5px] font-bold uppercase tracking-wider"><MapPin className="h-3 w-3" /> Booth {e.booth_number}</span>}
              </DialogDescription>
              <div className="mt-3">
                <ShareMenu
                  title={`${e.name} — AAWTA Summit Exhibitor`}
                  text={`${e.name}${e.booth_number ? ` · Booth ${e.booth_number}` : ''}${e.description ? ` · ${e.description.slice(0, 180).trimEnd() + (e.description.length > 180 ? '…' : '')}` : ''}`}
                  url={buildProfileUrl(e.id)}
                  emailSubject={`${e.name} — Booth ${e.booth_number || 'TBA'} at the AAWTA Workforce Summit`}
                  emailBody={`${e.name}${e.booth_number ? `\nBooth ${e.booth_number}` : ''}${e.description ? `\n\n${e.description}` : ''}\n\nView the exhibitor profile:`}
                  testIdPrefix={`exhibitor-modal-share-${e.id}`}
                />
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {e.description && (
            <div>
              <div className="overline text-aawta-muted">Organization Description</div>
              <p className="mt-1.5 text-[14.5px] leading-relaxed text-aawta-navy whitespace-pre-wrap">{e.description}</p>
            </div>
          )}
          {e.products && (
            <div>
              <div className="overline text-aawta-muted">Featured Products or Services</div>
              <p className="mt-1.5 text-[14.5px] leading-relaxed text-aawta-navy whitespace-pre-wrap">{e.products}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {website && (
              <div className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                <div className="overline text-aawta-muted">Website</div>
                <button onClick={() => openExternal(website)} data-testid={`exhibitor-modal-website-${e.id}`} className="mt-1 inline-flex items-center gap-1 text-aawta-navy font-medium hover:text-aawta-magenta break-all">
                  {e.website} <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {(e.contact_name || e.contact_email || e.contact_phone) && (
              <div className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                <div className="overline text-aawta-muted">Contact</div>
                {e.contact_name && <div className="mt-1 text-[13.5px] text-aawta-navy font-medium">{e.contact_name}</div>}
                {e.contact_email && <a href={`mailto:${e.contact_email}`} className="mt-0.5 text-[13px] text-aawta-navy hover:text-aawta-magenta inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {e.contact_email}</a>}
                {e.contact_phone && <a href={`tel:${e.contact_phone}`} className="mt-0.5 block text-[13px] text-aawta-navy hover:text-aawta-magenta"><Phone className="h-3 w-3 inline mr-1" /> {e.contact_phone}</a>}
              </div>
            )}
          </div>
          {e.qr_link && (
            <div className="p-4 rounded-lg bg-white border border-aawta-border flex items-center gap-4">
              <img src={qrUrl(e.qr_link)} alt="QR code" className="w-24 h-24 rounded" />
              <div>
                <div className="overline text-aawta-muted">Scan for more</div>
                <div className="text-[13px] text-aawta-navy break-all">{e.qr_link}</div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ExhibitorDirectory = ({ onOpenInquiry }) => {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    api.get('/exhibitors').then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  const hasItems = items && items.length > 0;

  return (
    <section id="exhibitors" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Exhibitor Directory</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              Meet the Organizations on the Exhibit Floor
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Explore the companies, education providers, workforce organizations, technology
              innovators, and community partners participating in the Future Workforce Summit.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="exhibitors-inquire-cta" onClick={() => onOpenInquiry('vendor')} className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all">
              <Store className="mr-2 h-4 w-4" /> Reserve Exhibit Space Now
            </Button>
            <Button data-testid="exhibitors-floorplan-cta" onClick={() => openExternal(AAWTA.exhibitorFloorPlanUrl)} variant="outline" className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6">
              <LayoutGrid className="mr-2 h-4 w-4" /> View Exhibitor Floor Plan
              <ExternalLink className="ml-2 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {hasItems ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((e) => {
              const website = normalizeUrl(e.website);
              return (
                <div key={e.id} id={`exhibitor-${e.id}`} data-testid={`exhibitor-card-${e.id}`} className="rounded-2xl bg-aawta-surface border border-aawta-border p-6 hover:shadow-aawta-elevated hover:-translate-y-0.5 transition-all duration-300 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-[88px] h-[88px] md:w-[120px] md:h-[120px] rounded-2xl bg-white border border-aawta-border flex items-center justify-center overflow-hidden p-2 shrink-0">
                      {e.logo_file_id ? (
                        <img
                          src={fileUrl(e.logo_file_id)}
                          alt={e.name}
                          className="max-w-full max-h-full w-auto h-auto object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <Store className="h-10 w-10 md:h-14 md:w-14 text-aawta-navy/40" />
                      )}
                    </div>
                    {e.qr_link && (
                      <img src={qrUrl(e.qr_link)} alt="QR code" className="w-16 h-16 rounded-lg bg-white p-1 border border-aawta-border" />
                    )}
                  </div>
                  <div className="mt-4">
                    <h3 className="font-display font-semibold text-lg text-aawta-navy">{e.name}</h3>
                    {e.industry && <div className="text-[12px] text-aawta-muted mt-0.5">{e.industry}</div>}
                  </div>
                  {e.description && <ExhibitorDesc description={e.description} id={e.id} />}
                  <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[10.5px] font-bold uppercase tracking-wider">
                      <MapPin className="h-3 w-3" /> Booth {e.booth_number || 'TBA'}
                    </div>
                    {website && (
                      <button onClick={() => openExternal(website)} data-testid={`exhibitor-website-${e.id}`} className="text-[12px] text-aawta-navy font-semibold hover:text-aawta-magenta inline-flex items-center gap-1">
                        Website <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-aawta-border flex items-center justify-between gap-2 flex-wrap">
                    <button
                      onClick={() => setSelected(e)}
                      data-testid={`exhibitor-fullprofile-${e.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-aawta-navy hover:bg-aawta-magenta text-white text-[12px] font-semibold px-4 py-1.5 transition-colors"
                    >
                      <Info className="h-3.5 w-3.5" /> View Full Profile
                    </button>
                    <ShareMenu
                      title={`${e.name} — AAWTA Summit Exhibitor`}
                      text={buildShareText(e)}
                      url={buildProfileUrl(e.id)}
                      emailSubject={`${e.name} — Booth ${e.booth_number || 'TBA'} at the AAWTA Workforce Summit`}
                      emailBody={`${buildShareText(e)}\n\nView the exhibitor profile:`}
                      testIdPrefix={`exhibitor-share-${e.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-aawta-surface border border-dashed border-aawta-border p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white border border-aawta-border flex items-center justify-center"><Store className="h-6 w-6 text-aawta-navy/40" /></div>
                </div>
                <div className="mt-5 space-y-2">
                  <div className="h-3 rounded-full bg-aawta-border w-3/4" />
                  <div className="h-2.5 rounded-full bg-aawta-border w-full" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selected && <FullProfile e={selected} onClose={() => setSelected(null)} />}
    </section>
  );
};

export default ExhibitorDirectory;
